import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap, retry, catchError, map, tap } from 'rxjs/operators';
import { LogService } from '@viag/ngx-logger';

import { TimeUtil } from '@shared/utils';
import { TimesheetApiService } from './timesheet-api.service';
import { TimesheetStorageService } from './timesheet-storage.service';
import { Hour } from '../types/hour';
import { Timesheet } from '../types/timesheet';

@Injectable({
  providedIn: 'root'
})
export class TimesheetService {
  private log = inject(LogService);
  private api = inject(TimesheetApiService);
  private storage = inject(TimesheetStorageService);


  getByDate(date: Date): Observable<Timesheet[]> {
    return this.api.getByDate(date)
      .pipe(
        retry(2),
        switchMap((timesheets: Timesheet[]) => {
          const today = new Date();
          const isToday = TimeUtil.compareDate(date, today);
          if (isToday) {
            timesheets.forEach(t => {
              t.startTime = new Date(Date.parse(t.startTime.toString()));
            });
            // replace cache with today's
            return this.storage.save(timesheets).pipe(
              map(_ => timesheets),
            );
          }
          return of(timesheets);
        }),
        catchError((error: HttpErrorResponse) => {
          this.log.warn('error catched, return from cache', error);
          return this.storage.get()
            .pipe(
              map(timesheets => {
                return timesheets
                  ? timesheets
                    .filter(t => TimeUtil.compareDate(t.startTime, date))
                    .sort((t1, t2) => t1.startTime.getTime() - t2.startTime.getTime())
                  : [];
              }),
              tap(timesheets => {
                this.log.info('from cache', timesheets);
              }),
            );
        }),
      );
  }

  add(hours: Hour[]): Observable<any> {
    return this.storage.get()
      .pipe(
        switchMap(timesheets => {
          hours.forEach(hour => {
            timesheets = this.update(hour, timesheets);
          });
          this.log.debug('storing timesheets', timesheets);
          return this.storage.save(timesheets);
        }),
      );
  }

  private update(hour: Hour, timesheets: Timesheet[]) {
    const existing = timesheets.find(t => t.timeSheetId === hour.projectTaskHourId);
    if (existing) {
      existing.endTime = hour.endTime;
      existing.duration = hour.duration;
    } else {
      const timesheet = {
        timeSheetId: hour.projectTaskHourId,
        projectNumber: hour.projectNumber,
        name: hour.taskName,
        startTime: hour.startTime,
        endTime: hour.endTime,
        duration: hour.duration,
        employeeId: null,
        modifiedOn: null,
        timeSheetTypeCode: 4,
        taskTypeCode: hour.taskTypeCode,
      };
      timesheets.unshift(timesheet);
    }
    return timesheets;
  }

}
